import React, { useRef, useState } from 'react';
import API_CONFIG from '../../apiConfig';
import { useAuth } from '../../context/AuthContext';
import CustomSelect from '../ui/CustomSelect';

interface VtonUploadResponse {
  success?: boolean;
  uploaded?: { id?: string; url: string }[];
  statusCode?: number;
  message?: string;
}

type VtonPlatform = 'fal' | 'replicate' | 'gemini';

interface VtonResponse {
  success: boolean;
  imageUrl?: string;
  generationId?: string;
  inputImageUrls?: string[];
  userId?: string;
  statusCode?: number;
  creditCost?: number;
  platform?: string;
  modelName?: string;
  message?: string;
}

const PLATFORM_OPTIONS: { value: VtonPlatform; label: string }[] = [
  { value: 'fal', label: 'Fal' },
  { value: 'replicate', label: 'Replicate' },
  { value: 'gemini', label: 'Gemini' },
];

/** Supported model_name values per platform (8 integrated models - see VTON_FRONTEND_REQUIREMENTS.md section 5) */
const SUPPORTED_MODELS: Record<VtonPlatform, { value: string; label: string }[]> = {
  fal: [
    { value: 'fal-ai/bytedance/seedream/v5/lite/edit', label: 'Seedream v5Lite edit' },
    { value: 'wan/v2.6/image-to-image', label: 'Wan v2.6 image-to-image' },
    { value: 'fal-ai/kling-image/o1', label: 'Kling image o1' },
    { value: 'fal-ai/bytedance/seedream/v4.5/edit', label: 'Seedream v4.5 edit' },
    { value: 'fal-ai/qwen-image-edit-2511', label: 'Qwen image edit 2511' },
  ],
  replicate: [
    { value: 'prunaai/p-image-edit', label: 'Pruna p-image-edit' },
    { value: 'black-forest-labs/flux-2-klein-4b', label: 'Flux 2 Klein 4b' },
  ],
  gemini: [
    { value: 'gemini-2.5-flash-image', label: 'Nano Banana 2.5 Flash' },
    { value: 'gemini-3.1-flash-image-preview', label: 'Nano Banana 3.1 Flash' },
    { value: 'gemini-3-pro-image-preview', label: 'Nano Banana 3 Pro' },
  ],
};

const DEFAULT_PROMPT = 'Replace the top from Image 1 with top in Image 2';

const IMAGE_LABELS = [
  'Image 1 (person)',
  'Image 2 (garment)',
  'Image 3',
  'Image 4',
];

const InternalVton: React.FC = () => {
  const { currentUser, refreshUserCredits } = useAuth();
  const [platform, setPlatform] = useState<VtonPlatform>('fal');
  const [modelName, setModelName] = useState(SUPPORTED_MODELS.fal[0].value);
  const [imageUrls, setImageUrls] = useState<string[]>(['', '', '', '']);
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingSlots, setUploadingSlots] = useState<number[]>([]);
  const [error, setError] = useState('');
  const [result, setResult] = useState<VtonResponse | null>(null);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);

  const updateImageUrl = (index: number, value: string) => {
    setImageUrls((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const getValidUrls = (): string[] => {
    return imageUrls.filter((u) => u.trim().length > 0);
  };

  const handleUpload = async (slotIndex: number, file: File) => {
    setError('');
    setUploadingSlots((prev) => [...prev, slotIndex]);
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('userId', currentUser?.uid || 'anonymous');

      const response = await fetch(API_CONFIG.VTON_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });

      const data: VtonUploadResponse = await response.json().catch(() => ({}));

      if (!response.ok || !data.success || !data.uploaded?.length) {
        const msg = data?.message || `Upload failed (${response.status})`;
        setError(msg);
        return;
      }

      updateImageUrl(slotIndex, data.uploaded[0].url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploadingSlots((prev) => prev.filter((s) => s !== slotIndex));
      if (fileInputRefs.current[slotIndex]) {
        fileInputRefs.current[slotIndex]!.value = '';
      }
    }
  };

  const onFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    handleUpload(index, file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    const urls = getValidUrls();
    if (urls.length < 1 || urls.length > 4) {
      setError('Please provide 1 to 4 image URLs.');
      return;
    }
    if (!modelName.trim()) {
      setError('Model name is required.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        platform,
        model_name: modelName.trim(),
        input_image_urls: urls,
        prompt: prompt.trim() || undefined,
        userId: currentUser?.uid || 'anonymous',
        internal_testing: false,
      };

      const response = await fetch(API_CONFIG.VTON_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data: VtonResponse = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg = data?.message || `Request failed (${response.status})`;
        setError(`${msg}${data?.statusCode != null ? ` [${data.statusCode}]` : ''}`);
        setResult(data);
        return;
      }

      setResult(data);
      if (!data.success) {
        setError(data.message || 'Generation failed.');
      } else {
        await refreshUserCredits();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network or request failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-accent-taupe rounded-lg text-text-heading bg-white outline-none focus:ring-1 focus:ring-secondary-200 focus:border-secondary-500 text-sm';
  const boxCls = 'rounded-xl border border-accent-taupe bg-white p-4 shadow-sm';

  return (
    <div className="min-h-[calc(100vh-8rem)] px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4">
          <h1 className="text-xl font-bold text-text-heading">VTON – Internal Testing</h1>
          <p className="text-xs text-text-description mt-0.5">
            Internal tools only. Credits are deducted per generation.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Form */}
          <div className="w-full lg:w-[420px] flex-shrink-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className={boxCls}>
                  <label className="block text-xs font-semibold text-text-heading uppercase tracking-wide mb-2">Platform</label>
                  <CustomSelect
                    value={platform}
                    onChange={(value) => {
                      const p = value as VtonPlatform;
                      setPlatform(p);
                      setModelName(SUPPORTED_MODELS[p][0].value);
                    }}
                    options={PLATFORM_OPTIONS}
                    className="border border-accent-taupe"
                  />
                </div>
                <div className={boxCls}>
                  <label className="block text-xs font-semibold text-text-heading uppercase tracking-wide mb-2">Model name</label>
                  <CustomSelect
                    value={modelName}
                    onChange={(value) => setModelName(value)}
                    options={SUPPORTED_MODELS[platform]}
                    className="border border-accent-taupe"
                  />
                </div>
              </div>

              <div className={boxCls}>
                <label className="block text-xs font-semibold text-text-heading uppercase tracking-wide mb-3">Input images (1–4)</label>
                <div className="grid grid-cols-2 gap-3">
                  {IMAGE_LABELS.map((label, i) => (
                    <div key={i} className="flex flex-col gap-1.5">
                      <span className="text-xs text-text-description">{label}</span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="file"
                          accept="image/*"
                          ref={(el) => { fileInputRefs.current[i] = el; }}
                          onChange={(e) => onFileChange(i, e)}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRefs.current[i]?.click()}
                          disabled={uploadingSlots.includes(i)}
                          className="px-2 py-1 text-xs border border-accent-taupe rounded-lg text-text-heading bg-white hover:bg-accent-sand disabled:opacity-50"
                        >
                          {uploadingSlots.includes(i) ? '…' : 'Upload'}
                        </button>
                        {imageUrls[i] && (
                          <div className="relative inline-block">
                            <img src={imageUrls[i]} alt="" className="w-14 h-14 rounded-lg border border-accent-taupe object-cover bg-accent-sand" />
                            <button
                              type="button"
                              onClick={() => updateImageUrl(i, '')}
                              className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 text-[10px]"
                              title="Remove"
                            >
                              <i className="fas fa-times" />
                            </button>
                          </div>
                        )}
                      </div>
                      <input
                        type="url"
                        value={imageUrls[i] ?? ''}
                        onChange={(e) => updateImageUrl(i, e.target.value)}
                        placeholder="Or paste URL"
                        className="w-full px-2 py-1.5 border border-accent-taupe rounded-lg text-text-heading bg-white outline-none focus:ring-1 focus:ring-secondary-500 text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className={boxCls}>
                <label className="block text-xs font-semibold text-text-heading uppercase tracking-wide mb-2">Prompt (optional)</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={2}
                  className={inputCls}
                />
              </div>

              <div className={boxCls}>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-5 py-2.5 bg-secondary-500 text-white rounded-lg font-semibold text-sm hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isLoading ? 'Generating…' : 'Generate'}
                </button>
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {error}
                </div>
              )}
            </form>
          </div>

          {/* Right: Result */}
          <div className="flex-1 min-w-0">
            <div className={`${boxCls} sticky top-4 min-h-[320px] flex flex-col`}>
              <h2 className="text-sm font-semibold text-text-heading uppercase tracking-wide mb-3">Generated result</h2>
              {result?.imageUrl ? (
                <>
                  <div className="flex-1 rounded-lg border border-accent-taupe overflow-hidden bg-accent-sand/30 flex items-center justify-center min-h-[280px] relative">
                    <img
                      src={result.imageUrl}
                      alt="VTON result"
                      className="max-w-full max-h-[480px] object-contain"
                    />
                    <button
                      className="absolute top-2 right-2 flex items-center justify-center w-8 h-8 bg-white/80 hover:bg-white text-text-heading hover:text-secondary-600 border border-accent-taupe rounded-lg shadow-sm transition-colors text-sm"
                      onClick={async () => {
                        try {
                          const response = await fetch(result.imageUrl as string);
                          const blob = await response.blob();
                          const blobUrl = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = `vton-result-${result.generationId || Date.now()}.jpg`;
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(blobUrl);
                        } catch (error) {
                          console.error('Download failed:', error);
                        }
                      }}
                      title="Download"
                    >
                      <i className="fas fa-download" />
                    </button>
                  </div>
                  <div className="mt-3 pt-3 border-t border-accent-taupe grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-text-description">
                    {result.platform != null && <span>Platform: <strong className="text-text-heading">{result.platform}</strong></span>}
                    {result.modelName != null && <span>Model: <strong className="text-text-heading truncate block">{result.modelName}</strong></span>}
                    {result.creditCost != null && <span>Credit cost: <strong className="text-text-heading">{result.creditCost}</strong></span>}
                    {result.generationId != null && <span className="col-span-2 truncate" title={result.generationId}>ID: {result.generationId}</span>}
                  </div>
                </>
              ) : (
                <div className="flex-1 rounded-lg border border-dashed border-accent-taupe bg-accent-sand/20 flex items-center justify-center min-h-[280px] text-text-description text-sm">
                  {isLoading ? (
                    <span className="flex items-center gap-2"><i className="fas fa-spinner fa-spin" /> Generating…</span>
                  ) : (
                    <span>Result will appear here after generation</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalVton;
